import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";

const CommandPalette = lazy(() =>
  import("@/components/search/CommandPalette").then((m) => ({ default: m.CommandPalette }))
);

function useBreadcrumb() {
  const { pathname } = useLocation();
  if (pathname === "/") return { label: "", mono: false };
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "flight") return { label: `Flyg · ${decodeURIComponent(parts[1] || "")}`, mono: true };
  if (parts[0] === "route") return { label: `Rutt · ${decodeURIComponent(parts[1] || "").replace("-", " → ")}`, mono: true };
  if (parts[0] === "airport") return { label: `Flygplats · ${decodeURIComponent(parts[1] || "")}`, mono: true };
  if (parts[0] === "reseservice") return { label: "Reseservice", mono: false };
  return { label: pathname, mono: false };
}

export function TopBar() {
  const [open, setOpen] = useState(false);
  const crumb = useBreadcrumb();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-40 h-12 sm:h-14 border-b border-rule bg-background/85 backdrop-blur-xl flex items-center gap-3 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="h-7 w-7 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor">
              <path d="M12 2 L14 11 L22 14 L22 16 L14 14.5 L13 21 L15 22 L15 23 L9 23 L9 22 L11 21 L10 14.5 L2 16 L2 14 L10 11 Z" />
            </svg>
          </div>
          <div className="font-display text-base font-semibold tracking-tight leading-none">
            Flyg<span className="text-muted-foreground">.info</span>
          </div>
        </Link>
        {crumb.label && (
          <div className={cn("hidden md:block text-xs text-muted-foreground truncate ml-3", crumb.mono && "font-mono uppercase tracking-wider")}>
            {crumb.label}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setOpen(true)}
            className="hidden sm:inline-flex items-center gap-2 h-8 rounded-md border border-rule bg-surface-2/60 hover:bg-surface-2 px-2.5 text-xs text-muted-foreground transition-colors min-w-[280px]"
          >
            <Search className="h-3.5 w-3.5" />
            <span>Sök flyg, rutt eller flygplats…</span>
            <kbd className="ml-auto font-mono text-[10px] bg-background border border-rule rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
          <button
            onClick={() => setOpen(true)}
            aria-label="Sök"
            className="sm:hidden h-8 w-8 rounded-md border border-rule bg-surface-2/60 hover:bg-surface-2 flex items-center justify-center text-muted-foreground"
          >
            <Search className="h-3.5 w-3.5" />
          </button>
          <span className="hidden md:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-60"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-success"></span>
            </span>
            Live
          </span>
        </div>
      </header>
      {open && (
        <Suspense fallback={null}>
          <CommandPalette open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </>
  );
}
